import React from "react";
import LegalLayout, { Section, List } from "./LegalLayout";
import { RegulatoryList, LinksList, MediationText } from "./RegulatoryInfo";
import { PHONE, EMAIL, ADDRESS } from "@/lib/siteData";

const H3 = ({ children }) => <h3 className="font-semibold text-md-primary pt-2">{children}</h3>;
const Warn = ({ children }) => <p className="border-l-2 border-md-secondary pl-4 font-medium text-md-primary">{children}</p>;

const FEES = [
  { label: "1 contrat d'assurance", value: "25 € TTC" },
  { label: "2 contrats d'assurance", value: "40 € TTC" },
  { label: "3 contrats d'assurance et plus", value: "50 € TTC" },
];

const WITHDRAWAL = [
  ["Prestation de courtage conclue à distance ou hors établissement", "14 jours de rétractation (art. L.221-18 et L.222-7 C. conso.)"],
  ["Assurance-vie, contrat de capitalisation, PER assurantiel", "30 jours de renonciation (art. L.132-5-1 C. assur.)"],
  ["Assurance (hors vie) souscrite à distance ou par démarchage", "14 jours de renonciation (art. L.112-2-1 et L.112-9 C. assur.), sous conditions"],
  ["Crédit immobilier", "Pas de rétractation : délai de réflexion de 10 jours après réception de l'offre, l'offre ne pouvant être acceptée avant (art. L.313-34 C. conso.)"],
  ["Crédit à la consommation", "14 jours de rétractation à compter de l'acceptation de l'offre (art. L.312-19 C. conso.)"],
];

const FeeTable = () => (
  <div className="grid sm:grid-cols-3 gap-3 my-2" data-testid="cgv-insurance-fees">
    {FEES.map((f) => (
      <div key={f.label} className="bg-white border border-md-border rounded-2xl p-5">
        <div className="font-serif text-3xl text-md-primary">{f.value}</div>
        <div className="text-xs uppercase tracking-widest text-md-muted mt-1">{f.label}</div>
      </div>
    ))}
  </div>
);

const WithdrawalTable = () => (
  <div className="bg-white border border-md-border rounded-2xl divide-y divide-md-border" data-testid="cgv-withdrawal-table">
    {WITHDRAWAL.map(([k, v]) => (
      <div key={k} className="grid sm:grid-cols-2 gap-1 px-5 py-3.5 text-sm">
        <span className="font-medium text-md-primary">{k}</span>
        <span className="text-md-muted">{v}</span>
      </div>
    ))}
  </div>
);

export default function Cgv() {
  return (
    <LegalLayout eyebrow="Informations légales" title="Conditions générales de vente" testId="cgv-page">
      <Section n="1" title="Objet et documents contractuels">
        <p>
          Les présentes conditions générales de vente (« CGV ») régissent les prestations d'intermédiation et de conseil
          fournies par MD Courtage à ses clients particuliers et professionnels, en crédit, assurance et gestion de patrimoine.
          Avant toute prestation, vous recevez un <strong>document d'entrée en relation (DER)</strong> ; selon la prestation, un
          mandat de recherche de capitaux ou une lettre de mission est ensuite signé. Ces documents prévalent sur les CGV en
          cas de contradiction.
        </p>
      </Section>

      <Section n="2" title="Identité, statuts et liens du courtier">
        <p>MD Courtage — Maël DUBOIS, entrepreneur individuel, RCS Paris n° 812 084 259 — {ADDRESS} — {PHONE} — {EMAIL}.</p>
        <RegulatoryList />
        <LinksList />
      </Section>

      <Section n="3" title="Nature des prestations">
        <H3>3.1 Crédit</H3>
        <p>Analyse de votre capacité d'emprunt, montage du dossier, mise en concurrence des établissements prêteurs partenaires, négociation et accompagnement jusqu'au déblocage des fonds.</p>
        <Warn>Un crédit vous engage et doit être remboursé. Vérifiez vos capacités de remboursement avant de vous engager. Aucun versement, de quelque nature que ce soit, ne peut être exigé d'un particulier avant l'obtention d'un ou plusieurs prêts d'argent.</Warn>
        <H3>3.2 Assurance</H3>
        <p>Recueil de vos exigences et besoins, comparaison des offres des assureurs partenaires, conseil sur le contrat le plus adapté, accompagnement à la souscription et suivi.</p>
        <H3>3.3 Gestion de patrimoine et placement</H3>
        <p>Bilan patrimonial et conseil en investissement, formalisés par une lettre de mission et un rapport d'adéquation, puis suivi dans la durée. Le conseil est fourni de manière non indépendante (article 2).</p>
        <Warn>Les placements présentent un risque de perte en capital. Les performances passées ne préjugent pas des performances futures.</Warn>
        <p>
          MD Courtage est tenu d'une obligation de moyens et d'un devoir de conseil. Il ne peut garantir l'obtention d'un
          financement, l'acceptation d'un risque par un assureur ni la performance d'un placement. La liste des partenaires
          n'est pas exhaustive du marché ; elle est communiquée sur demande et dans le DER.
        </p>
      </Section>

      <Section n="4" title="Honoraires et rémunération">
        <p>Tous les prix sont indiqués <strong>toutes taxes comprises (TTC)</strong>.</p>
        <H3>4.1 Courtage en crédit</H3>
        <p>
          Les frais de courtage sont à la charge du client. Leur montant, forfaitaire ou proportionnel au montant financé, est
          chiffré dans le mandat de recherche de capitaux, communiqué et accepté avant sa signature. Ils ne sont exigibles
          qu'après le déblocage effectif des fonds : <strong>si aucun prêt n'est obtenu, aucun frais n'est dû</strong>. Lorsque MD
          Courtage perçoit également une commission de l'établissement prêteur, son existence, sa nature et son montant (ou
          mode de calcul) vous sont communiqués avant la signature du mandat.
        </p>
        <H3>4.2 Courtage en assurance</H3>
        <p>Des frais de courtage forfaitaires sont facturés par dossier :</p>
        <FeeTable />
        <p>
          Ils rémunèrent la recherche, la comparaison et le conseil, et s'ajoutent à la commission versée par l'assureur,
          incluse dans la prime. Ils sont présentés et acceptés par écrit avant toute souscription. Lorsque la réglementation
          encadre ou interdit des frais pour un type de contrat, ils ne sont pas facturés. Leur sort en cas de rétractation
          est précisé à l'article 6.
        </p>
        <H3>4.3 Gestion de patrimoine et placement</H3>
        <p>
          <strong>Aucun honoraire n'est facturé directement au client.</strong> MD Courtage est rémunéré par les sociétés
          partenaires (assureurs, sociétés de gestion) par des commissions prélevées sur les frais d'entrée et/ou de gestion
          des contrats, ce qui en fait un conseil non indépendant. Le détail des coûts et commissions vous est communiqué
          avant toute souscription, puis chaque année, et sur simple demande.
        </p>
      </Section>

      <Section n="5" title="Modalités de paiement">
        <List items={[
          "Paiement par virement bancaire (coordonnées indiquées sur la facture) ou par chèque à l'ordre de MD Courtage, à réception de la facture.",
          "Aucun escompte n'est accordé en cas de paiement anticipé.",
          "Clients professionnels : tout retard entraîne de plein droit des pénalités égales à trois fois le taux d'intérêt légal, ainsi qu'une indemnité forfaitaire de 40 € pour frais de recouvrement (article L.441-10 du Code de commerce).",
        ]} />
      </Section>

      <Section n="6" title="Droit de rétractation et de renonciation">
        <p>
          Lorsque la prestation de courtage est conclue à distance ou hors établissement, le client consommateur peut se
          rétracter pendant 14 jours, sans motif ni pénalité, en écrivant à {EMAIL} ou par courrier. Les frais de courtage en
          assurance sont alors <strong>remboursés intégralement</strong> sous 14 jours.
        </p>
        <p>
          Exception : si vous avez demandé par écrit (case dédiée ou mention signée) que la prestation commence avant la fin du
          délai et que le contrat d'assurance a été souscrit, la prestation est pleinement exécutée et les frais restent
          acquis. Sans cette demande écrite, aucun montant ne peut vous être réclamé pendant le délai (article L.221-25 du Code
          de la consommation). Les délais propres aux contrats souscrits s'appliquent en outre :
        </p>
        <WithdrawalTable />
      </Section>

      <Section n="7" title="Obligations du client">
        <List items={[
          "Fournir des informations exactes, complètes et sincères, ainsi que les justificatifs demandés.",
          "Signaler sans délai tout changement de situation (personnelle, professionnelle, patrimoniale, de santé lorsque requis) susceptible de modifier le conseil ou les garanties.",
          "En assurance, toute fausse déclaration intentionnelle entraîne la nullité du contrat (article L.113-8 du Code des assurances) ; une omission ou déclaration inexacte non intentionnelle peut entraîner une réduction des indemnités (article L.113-9).",
        ]} />
      </Section>

      <Section n="8" title="Responsabilité">
        <p>
          MD Courtage est tenu d'une obligation de moyens et est couvert par son assurance responsabilité civile
          professionnelle (article 2). Pour les clients professionnels, sa responsabilité est limitée au montant des frais
          perçus au titre de la mission concernée, sauf faute lourde ou dolosive. Aucune limitation n'est opposable aux clients
          consommateurs au-delà de ce que prévoit la loi, ni en cas de dommage corporel.
        </p>
      </Section>

      <Section n="9" title="Durée et résiliation">
        <p>
          La relation débute à la signature du mandat ou de la lettre de mission et prend fin à l'accomplissement de la mission
          (déblocage des fonds, souscription du contrat, ou terme de la lettre de mission pour le suivi patrimonial). Le client
          peut y mettre fin à tout moment par écrit, sans préavis ; seuls restent dus les frais acquis selon les articles 4 et 6.
          MD Courtage peut y mettre fin par écrit avec un préavis de 15 jours, sauf manquement grave du client.
        </p>
      </Section>

      <Section n="10" title="Prospection et consentement">
        <p>
          MD Courtage ne réalise aucun démarchage téléphonique sans votre consentement préalable, recueilli séparément des
          présentes CGV, valable 12 mois maximum et retirable à tout moment. Le dispositif d'opposition Bloctel a pris fin avec
          l'entrée en vigueur de ce régime au 11 août 2026. Modalités détaillées : article 6 des <a href="/cgu" className="underline text-md-primary">CGU</a>.
        </p>
      </Section>

      <Section n="11" title="Réclamations et médiation">
        <p>
          Toute réclamation doit d'abord être adressée par écrit à {EMAIL} ou par courrier au {ADDRESS}. MD Courtage en accuse
          réception sous 10 jours ouvrables et y répond dans un délai maximal de 2 mois.
        </p>
        <MediationText />
        <List items={[
          "Le médiateur peut être saisi après une réclamation écrite préalable restée sans réponse satisfaisante, ou sans réponse dans un délai de 2 mois, et au plus tard un an après cette réclamation. La médiation est gratuite pour le consommateur.",
          "Pour les litiges relatifs au conseil en investissements financiers, vous pouvez également saisir le Médiateur de l'AMF — 17 place de la Bourse, 75082 Paris Cedex 02 — www.amf-france.org.",
          "Vous pouvez signaler un manquement professionnel à l'ACPR (crédit, assurance) ou à l'AMF (placements).",
        ]} />
      </Section>

      <Section n="12" title="Dispositions générales">
        <List items={[
          "Force majeure : aucune partie n'est responsable d'un manquement résultant d'un cas de force majeure au sens de l'article 1218 du Code civil.",
          "Sous-traitance : MD Courtage peut recourir à des prestataires techniques ou à ses mandants (INOVEA, RB COURTAGE) pour l'exécution de certaines tâches, sous sa responsabilité et dans le respect du secret professionnel.",
          "Intégralité et nullité partielle : les CGV, le DER et le mandat ou la lettre de mission forment l'intégralité de l'accord. La nullité d'une clause n'affecte pas les autres.",
        ]} />
      </Section>

      <Section n="13" title="Données personnelles, droit applicable et juridiction">
        <p>
          Les données sont traitées conformément à la <a href="/confidentialite" className="underline text-md-primary">politique de confidentialité</a>.
          Les présentes CGV sont soumises au droit français. Le consommateur peut saisir, à son choix, la juridiction du lieu où
          il demeurait au moment de la conclusion du contrat ou de la survenance du fait dommageable, ou toute juridiction
          compétente selon la loi (article R.631-3 du Code de la consommation). Pour les clients professionnels, compétence
          exclusive est attribuée aux tribunaux de Paris.
        </p>
      </Section>
    </LegalLayout>
  );
}
